export type SpriteAccent = "mint" | "violet" | "cyan" | "amber" | "slate";

export type ToolsPayload = {
  project_name: string;
  global_root: string;
  global_ready: boolean;
  groups: ToolGroupView[];
  selected: CapabilityView;
  import_feedback?: ImportFeedbackView | null;
};

export type ToolGroupView = {
  label: string;
  color: string;
  entries: ToolListItem[];
};

export type ToolListItem = {
  id: string;
  name: string;
  summary: string;
  icon_svg?: string | null;
  selected: boolean;
  global_state: string;
  project_state: string;
  status: string;
};

export type CapabilityView = {
  id: string;
  name: string;
  kind_label: string;
  summary: string;
  source: string;
  integration: string;
  icon_svg?: string | null;
  locked: boolean;
  available_exists: boolean;
  enabled: boolean;
  available_path: string;
  enabled_path: string;
  provides: string[];
  required_env: string[];
  binaries: string[];
  endpoints: string[];
  otel?: OtelView | null;
  install_checks: CheckView[];
  tester_checks: CheckView[];
};

export type OtelView = {
  service_name: string;
  service_namespace: string;
  service_version: string;
  signals: string[];
  propagation: string[];
  resource_attributes: OtelAttributeView[];
  span_attributes: string[];
  span_metrics: OtelMetricView[];
  collector_sections: string[];
  live_tap?: OtelLiveTapView | null;
};

export type OtelAttributeView = {
  key: string;
  value: string;
};

export type OtelMetricView = {
  name: string;
  description: string;
  dimensions: string[];
};

export type OtelLiveTapView = {
  enabled: boolean;
  transport: string;
  retention: string;
  storage: string;
  location: string;
  topic: string;
};

export type CheckView = {
  label: string;
  detail: string;
  tone: "good" | "warn" | "muted";
};

export type ImportFeedbackView = {
  tone: "good" | "warn" | "muted";
  title: string;
  detail: string;
  kind_label: string;
  destination: string;
};

export const fallbackPayload: ToolsPayload = {
  project_name: "workspace",
  global_root: "~/.opentool/tools_available",
  global_ready: true,
  groups: [],
  selected: {
    id: "tools",
    name: "Tools",
    kind_label: "capability",
    summary: "Capability notes are loading.",
    source: "local",
    integration: "local",
    icon_svg: null,
    locked: false,
    available_exists: false,
    enabled: false,
    available_path: "",
    enabled_path: "",
    provides: [],
    required_env: [],
    binaries: [],
    endpoints: [],
    otel: null,
    install_checks: [],
    tester_checks: [],
  },
  import_feedback: null,
};

export function accentForGroup(label: string): SpriteAccent {
  const key = label.toLowerCase();
  if (key.includes("standard")) return "mint";
  if (key.includes("skill")) return "violet";
  if (key.includes("tool")) return "cyan";
  if (key.includes("mcp")) return "amber";
  if (key.includes("workflow")) return "violet";
  if (key.includes("app")) return "cyan";
  if (key.includes("provider")) return "mint";
  if (key.includes("cli")) return "cyan";
  if (key.includes("plugin")) return "mint";
  return "slate";
}

export function spriteForId(id: string): string[] {
  const sum = Array.from(id).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const shapes = [
    ["1110", "1011", "1111", "0110"],
    ["0110", "1111", "1111", "0010"],
    ["1111", "0011", "0110", "1100"],
    ["1110", "1000", "1110", "1111"],
    ["0110", "1110", "1110", "0110"],
  ];
  return shapes[sum % shapes.length];
}

export function hasGlobalInstall(item: ToolListItem) {
  const state = item.global_state.toLowerCase();
  return state.includes("installed") || state.includes("ready") || state.includes("linked");
}

export function projectEnabled(item: ToolListItem) {
  const state = item.project_state.toLowerCase();
  return state.includes("enabled") || state.includes("locked") || state.includes("on");
}

export function capabilitySummary(capability: CapabilityView) {
  if (capability.locked) return "Built into every managed project.";
  if (capability.enabled) return "Live in this project now.";
  if (capability.available_exists) return "Installed globally. Toggle it on here when needed.";
  return "Catalog only. Install it globally to use it.";
}

export function noteTone(capability: CapabilityView) {
  if (capability.tester_checks.some((check) => check.tone === "warn")) return "warn";
  if (capability.available_exists && (capability.enabled || capability.locked)) return "good";
  return "quiet";
}

export function noteLabel(capability: CapabilityView) {
  if (capability.locked) return "locked on";
  if (capability.enabled) return "project on";
  if (capability.available_exists) return "global only";
  return "catalog";
}
